import "./styles/main.scss";
import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Header from "./components/Header";
// import Footer from "./components/Footer"; // Unused for now
import { ThemeProvider } from "./context/ThemeContext";
import HeadingAnimator from "./components/HeadingAnimator";
import CookieBanner from "./components/CookieBanner";
import { CookieConsentProvider } from "./context/CookieConsentContext";
import HotjarInit from "./components/HotjarInit";
import SvgPathDrawOnLoad from "./components/SvgPathDrawOnLoad";
import Loader from "./components/Loader";
import i18n from "./i18n";

// Lazy load routes - only Home is loaded immediately
const Home = lazy(() => import("./pages/Home"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const BrandingLogoArticle = lazy(() => import("./pages/BrandingLogoArticle"));

// Language route wrapper - syncs URL path with i18n language
const LanguageRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathLang = location.pathname.startsWith('/en') ? 'en' : 'pl';
  const [isLanguageReady, setIsLanguageReady] = React.useState(i18n.language === pathLang);
  const previousPathRef = React.useRef(location.pathname);
  
  useEffect(() => {
    // Check if i18n language doesn't match the path - this indicates an intentional language switch
    const i18nLang = i18n.language;
    const pathLangFromPath = location.pathname.startsWith('/en') ? 'en' : 'pl';
    
    // If i18n language doesn't match path, prioritize i18n language (user clicked language switcher)
    if (i18nLang !== pathLangFromPath) {
      // User intentionally switched language, update path to match
      const wasOnEnglish = previousPathRef.current.startsWith('/en');
      const currentPath = location.pathname;
      const basePath = wasOnEnglish ? currentPath.replace('/en', '') || '/' : currentPath;
      
      if (i18nLang === 'en') {
        // Switching to English - add /en prefix
        const newPath = `/en${basePath === '/' ? '' : basePath}${location.search}${location.hash}`;
        if (newPath !== currentPath) {
          previousPathRef.current = newPath;
          navigate(newPath, { replace: true });
          return;
        }
      } else {
        // Switching to Polish - remove /en prefix
        if (currentPath.startsWith('/en')) {
          const newPath = `${basePath}${location.search}${location.hash}`;
          previousPathRef.current = newPath;
          navigate(newPath, { replace: true });
          return;
        }
      }
    }
    
    // If navigating from /en to a path without /en (and not an intentional language switch), preserve the /en prefix
    const wasOnEnglish = previousPathRef.current.startsWith('/en');
    const isNowOnEnglish = location.pathname.startsWith('/en');
    
    if (wasOnEnglish && !isNowOnEnglish && location.pathname !== '/' && i18nLang === 'en') {
      // User was on English version and still wants English, redirect to English version of this path
      const newPath = `/en${location.pathname}${location.search}${location.hash}`;
      previousPathRef.current = newPath;
      navigate(newPath, { replace: true });
      return;
    }
    
    // Update previous path reference
    previousPathRef.current = location.pathname;
    
    // If language matches path, we're ready
    if (i18n.language === pathLang) {
      setIsLanguageReady(true);
      return;
    }
    
    setIsLanguageReady(false);
    
    // If switching to English, ensure translations are loaded first
    if (pathLang === 'en') {
      // Check if English is already loaded
      if (!i18n.hasResourceBundle('en', 'translation')) {
        // Load English translations first, then change language
        import("./locales/en.json")
          .then((en) => {
            i18n.addResourceBundle('en', 'translation', en.default || en);
            return i18n.changeLanguage('en');
          })
          .then(() => {
            try {
              localStorage.setItem("i18nextLng", 'en');
            } catch {}
            setIsLanguageReady(true);
          })
          .catch(() => {
            console.warn("Failed to load English translations");
            // Fallback to Polish
            i18n.changeLanguage('pl').then(() => {
              setIsLanguageReady(true);
            });
          });
      } else {
        // English already loaded, just switch
        i18n.changeLanguage('en').then(() => {
          try {
            localStorage.setItem("i18nextLng", 'en');
          } catch {}
          setIsLanguageReady(true);
        });
      }
    } else {
      // Switch to Polish
      i18n.changeLanguage('pl').then(() => {
        try {
          localStorage.setItem("i18nextLng", 'pl');
        } catch {}
        setIsLanguageReady(true);
      });
    }
  }, [location.pathname, pathLang, navigate]);
  
  // Show loader while language is being switched
  if (!isLanguageReady) {
    return <Loader />;
  }
  
  return <>{children}</>;
};

// Lazy load Swiper CSS only when needed (will be imported in components that use it)
// Removed global Swiper CSS imports - will be loaded on-demand
 
// Removed glitch-related components
// import CareGlitchLoop from "./components/CareGlitchLoop";
// import AssignGlitchHalf from "./components/AssignGlitchHalf";
// import TargetedGlitch from "./components/TargetedGlitch";
// import SvgGlitchLoop from "./components/SvgGlitchLoop";
// import BannerifyHeadings from "./components/BannerifyHeadings";

// Removed Reset/Matrix effects

const App = () => (
  <HelmetProvider>
    <ThemeProvider>
      <CookieConsentProvider>
      <Router>
        <LanguageRoute>
          <div className="app">
            <HotjarInit />
            {/* Global path draw animator (listens for loader:finished) */}
            <SvgPathDrawOnLoad />
            <Header />
            <Suspense fallback={<Loader />}>
              <Routes>
                {/* Polish routes */}
                <Route path="/" element={<Home />} />
                <Route path="/home" element={<Home />} />
                <Route path="/cookie-policy" element={<CookiePolicy />} />
                <Route
                  path="/wiedza/branding/logo-ktore-dba-o-twoj-wizerunek"
                  element={<BrandingLogoArticle />}
                />
                {/* English routes */}
                <Route path="/en" element={<Home />} />
                <Route path="/en/home" element={<Home />} />
                <Route path="/en/cookie-policy" element={<CookiePolicy />} />
                <Route
                  path="/en/wiedza/branding/logo-ktore-dba-o-twoj-wizerunek"
                  element={<BrandingLogoArticle />}
                />
              </Routes>
            </Suspense>
            <HeadingAnimator />
            <CookieBanner />
          </div>
        </LanguageRoute>
      </Router>
      </CookieConsentProvider>
    </ThemeProvider>
  </HelmetProvider>
);

export default App;
